/* eslint-disable react/prop-types */
import './KYCView.scss';
import { useContext, useState, useEffect } from 'react';
import Moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faChevronRight,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import XpektorButton from '../../../../../Components/XpektorButton/XpektorButton';
import ArrowTab from '../../../../../Components/ArrowTab/ArrowTab';
import CustomerService from '../../../../../Services/CustomerService';
import UserService from '../../../../../Services/UserService';
import AddSupportInfoModal from '../../../Modals/AddSupportInfoModal';
import { TranslateKYCTriggers } from '../../../../../Components/Utilities/KYCTriggerTexts';
import ConfirmActionModal from '../../../Modals/ConfirmActionModal';
import AuthContext from '../../../../../Context/AuthContext';

const ignoredTriggerIds = [3, 4, 5, 6, 31, 32];
const warningTriggerIds = [1, 2];
const ACTION = Object.freeze({
  NEW: 'new',
  CANCEL: 'cancel',
});

const gT = (t, key) => t(`company-info-modules.regulatory.kyc-view${key}`);
const gsT = (t, key) => t(`shared${key}`);

function BoardMemberDeviationTrigger({ deviation, t }) {
  return (
    <div className="trigger-container">
      <div className="trigger-content">
        <p className="trigger-title error">
          {gT(t, '.board-member-deviation-trigger.trigger-title')}
        </p>
        <p className="trigger-text">
          <b>{deviation.name}</b>&nbsp;
          {deviation.type === 1
            ? gT(t, '.board-member-deviation-trigger.trigger-text.0')
            : gT(t, '.board-member-deviation-trigger.trigger-text.1')}
        </p>
      </div>
    </div>
  );
}

function WebsiteTrigger({ websiteHit, t }) {
  let hitCategories = '';
  if (websiteHit.categories !== null && websiteHit.categories.length > 0) {
    for (let i = 0; i < websiteHit.categories.length; i += 1) {
      hitCategories = `${hitCategories} ${websiteHit.categories[i]}`;
      if (i < websiteHit.categories.length - 1) {
        hitCategories = `${hitCategories},`;
      }
    }
  }

  return (
    <div className="trigger-container">
      <div className="trigger-content">
        <p className="trigger-title error">
          {gT(t, '.website-trigger.trigger-title')}
        </p>
        <p className="trigger-text">
          {gT(t, '.website-trigger.trigger-text.0')}&nbsp;
          <b>
            {websiteHit.websiteType === 1
              ? gT(t, '.website-trigger.trigger-text.1')
              : gT(t, '.website-trigger.trigger-text.2')}
          </b>
          &nbsp;
          {gT(t, '.website-trigger.trigger-text.3')}
          {hitCategories.length > 0 && (
            <p className="trigger-text">
              {gT(t, '.website-trigger.trigger-text.4')} {hitCategories}
            </p>
          )}
        </p>
      </div>
    </div>
  );
}

function ScreeningResultTrigger({ result, onClick, t }) {
  return (
    <button
      type="button"
      className="trigger-container trigger-button"
      onClick={onClick}
    >
      <div className="trigger-content">
        <p className="trigger-title warning">
          {gT(t, '.screening-result-trigger.trigger-title')}
        </p>
        <p className="trigger-text">
          {gT(t, '.screening-result-trigger.trigger-text.0')}&nbsp;
          <b>{result.name}</b>.
        </p>
      </div>
      <FontAwesomeIcon className="trigger-button-icon" icon={faChevronRight} />
    </button>
  );
}

function StandaloneTrigger({ trigger, t }) {
  const triggerType = warningTriggerIds.includes(trigger.id)
    ? 'warning'
    : 'error';
  return (
    <div className="trigger-container">
      <div className="trigger-content">
        <p className={`trigger-title ${triggerType}`}>
          {triggerType === 'warning'
            ? gT(t, '.standalone-trigger.warning')
            : gT(t, '.standalone-trigger.deviation')}
        </p>
        <p className="trigger-text">{trigger.texts.name}</p>
      </div>
    </div>
  );
}

export default function KYCView({
  kycStatus,
  onCreateClick,
  onCancelClick,
  onResultClick,
  isImpersonating,
  t,
}) {
  const [kycFormUrl, setKYCFormUrl] = useState('');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [showAddSupportInfoModal, setShowAddSupportInfoModal] = useState(false);
  const [customerData, setCustomerData] = useState({});
  const [translatedTriggers, setTranslatedTriggers] = useState([]);
  const [animatingCopied, setAnimatingCopied] = useState(false);
  const [animatingCopiedCount, setAnimatingCopiedCount] = useState(0);
  const [showConfirmActionModal, setShowConfirmActionModal] = useState(false);
  const [confirmActionText, setConfirmActionText] = useState('');
  const [confirmAction, setConfirmAction] = useState('');
  const [authState] = useContext(AuthContext);

  useEffect(() => {
    if (kycStatus !== null && Object.keys(kycStatus).length > 0) {
      setKYCFormUrl(
        `${window.location.protocol}//${window.location.host}/kyc-form?state=${kycStatus.stateId}`
      );
      const triggersToTranslate = kycStatus.triggers.filter(
        (trigger) => !ignoredTriggerIds.includes(Number(trigger))
      );
      setTranslatedTriggers(TranslateKYCTriggers(triggersToTranslate));
      setIsCreatingNew(false);
    }
  }, [kycStatus]);

  useEffect(() => {
    const animateCopied = () => {
      const newAnimatingCopiedCount = animatingCopiedCount + 1;
      if (newAnimatingCopiedCount === 2) {
        setAnimatingCopiedCount(0);
        setAnimatingCopied(false);
      } else {
        setAnimatingCopiedCount(newAnimatingCopiedCount);
      }
    };
    const timer = setTimeout(() => animatingCopied && animateCopied(), 1000);
    return () => clearTimeout(timer);
  }, [animatingCopiedCount, animatingCopied]);

  const validateCustomerAndCreateNewProcess = async () => {
    setIsCreatingNew(true);

    const user = await UserService.getCurrentUser();
    await CustomerService.getCustomer(user.customerId)
      .then((response) => {
        if (!response.data.supportPhoneNumber || !response.data.supportEmail) {
          setCustomerData(response.data);
          setShowAddSupportInfoModal(true);
          return;
        }
        onCreateClick();
      })
      .catch(() => {});
  };

  const updateCustomerAndCreateNewProcess = async (supportInfo) => {
    const newCustomer = { ...customerData };
    newCustomer.supportEmail = supportInfo.email;
    newCustomer.supportPhoneNumber = supportInfo.phone;

    await CustomerService.updateCustomer(newCustomer)
      .then(() => {
        onCreateClick();
        setShowAddSupportInfoModal(false);
      })
      .catch(() => {});
  };

  const translateFormType = (type) => {
    /* eslint-disable prettier/prettier */
    switch (type) {
      case 0:
        return gT(t, '.translate-form-type.0');
      case 1:
        return gT(t, '.translate-form-type.1');
      case 2:
        return gT(t, '.translate-form-type.2');
      case 3:
        return gT(t, '.translate-form-type.3');
      case 4:
        return gT(t, '.translate-form-type.4');
      default:
        return gsT(t, '.labels.not-available');
    }
    /* eslint-enable prettier/prettier */
  };

  return (
    <div className="kyc-overview-wrapper">
      <div className="main-column">
        <div className="title-column">
          <div className="title-row">
            <h2>{gT(t, '.title-row.header')}</h2>
            <p className="info-text-panel">
              <FontAwesomeIcon icon={faInfoCircle} className="info-text-icon" />
              {gT(t, '.title-row.info-text-panel')}
            </p>
          </div>
          <div className="status-steps">
            <ArrowTab
              text={gT(t, '.arrow-tab-text.status-1')}
              inactive={kycStatus === null || kycStatus.state !== 1}
              color="#3e66fb"
            />
            <ArrowTab
              text={gT(t, '.arrow-tab-text.status-2')}
              inactive={kycStatus === null || kycStatus.state !== 2}
              color="#ECA60F"
            />
            <ArrowTab
              text={gT(t, '.arrow-tab-text.status-3')}
              inactive={kycStatus === null || kycStatus.state < 3}
              color="#006400"
            />
          </div>
        </div>
        {kycStatus === null ? (
          <p className="empty-text">{gT(t, '.empty-text.no-kyc-process')}</p>
        ) : Object.keys(kycStatus).length === 0 ? (
          <p className="empty-text">{gT(t, '.empty-text.fetching')}</p>
        ) : (
          <div className="data-column">
            <div className="entry-column">
              <p className="data-title">{gT(t, '.data-title.form-type')}</p>
              <p>
                {kycStatus.formType !== null
                  ? translateFormType(kycStatus.formType)
                  : gsT(t, '.labels.not-available')}
              </p>
            </div>
            <div className="entry-column">
              <p className="data-title">{gT(t, '.data-title.date-created')}</p>
              <p>
                {Moment.utc(kycStatus.dateCreated).local().format('YYYY-MM-DD')}
              </p>
            </div>
            <div className="entry-column">
              <p className="data-title">{gT(t, '.data-title.last-accessed')}</p>
              <p>
                {kycStatus.lastAccessed
                  ? Moment.utc(kycStatus.lastAccessed)
                      .local()
                      .format('YYYY-MM-DD')
                  : gsT(t, '.labels.not-available')}
              </p>
            </div>
            <div className="entry-column">
              <p className="data-title">{gT(t, '.data-title.date-sealed')}</p>
              <p>
                {kycStatus.dateSealed
                  ? Moment.utc(kycStatus.dateSealed)
                      .local()
                      .format('YYYY-MM-DD')
                  : gsT(t, '.labels.not-available')}
              </p>
            </div>
          </div>
        )}
        <div className="action-row">
          {kycStatus === null ? (
            <>
              <XpektorButton
                content={gT(t, '.xpektor-btn.create-new-process')}
                onClick={() => validateCustomerAndCreateNewProcess()}
                loading={isCreatingNew}
                disabled={
                  isImpersonating || !authState.user || authState.user.role < 1
                }
              />
              <p className="info-text">
                <i>{gT(t, '.info-text.for-each-kyc-cost')}</i>
              </p>
            </>
          ) : kycStatus.state === 1 ? (
            <>
              <XpektorButton
                icon={!animatingCopied ? faCopy : ''}
                content={
                  animatingCopied
                    ? gT(t, '.xpektor-btn.copied')
                    : gT(t, '.xpektor-btn.copy-link-to-form')
                }
                onClick={() => {
                  navigator.clipboard.writeText(kycFormUrl);
                  setAnimatingCopied(true);
                }}
              />
              <XpektorButton
                content={gT(t, '.xpektor-btn.open-form')}
                onClick={() => {
                  window.open(kycFormUrl, '_blank', 'noopener,noreferrer');
                }}
              />
            </>
          ) : kycStatus.state === 2 ? (
            <XpektorButton
              content={gT(t, '.xpektor-btn.cancel')}
              variant="outline-red"
              onClick={() => {
                setConfirmAction(ACTION.CANCEL);
                setConfirmActionText(
                  gT(t, '.confirm-action-text.cancel-signing-process')
                );
                setShowConfirmActionModal(true);
              }}
              disabled={
                isImpersonating || !authState.user || authState.user.role < 1
              }
            />
          ) : (
            kycStatus.state >= 3 && (
              <>
                <XpektorButton
                  content={gT(t, '.xpektor-btn.download-link')}
                  variant="outline"
                  onClick={() => {
                    if (kycStatus.downloadLink) {
                      window.open(
                        kycStatus.downloadLink,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }
                  }}
                />
                <div className="new-process-content">
                  <XpektorButton
                    content={gT(t, '.xpektor-btn.start-new-process')}
                    onClick={() => {
                      setConfirmAction(ACTION.NEW);
                      setConfirmActionText(
                        gT(t, '.confirm-action-text.start-new-process')
                      );
                      setShowConfirmActionModal(true);
                    }}
                    disabled={
                      isImpersonating ||
                      !authState.user ||
                      authState.user.role < 1
                    }
                  />
                </div>
              </>
            )
          )}
        </div>
      </div>
      <div className="trigger-column">
        <p className="trigger-column-title">
          {gT(t, '.trigger-column-title.warnings-deviations')}
        </p>
        {kycStatus !== null &&
          Object.keys(kycStatus).length !== 0 &&
          kycStatus.state >= 3 && (
            <div className="trigger-list">
              {kycStatus.state === 3 ? (
                <div className="empty-triggers-sign in-progress">
                  <p>{gT(t, '.empty-triggers-sign.in-progress')}</p>
                </div>
              ) : kycStatus.triggers.length === 0 ? (
                <div className="empty-triggers-sign no-triggers">
                  <FontAwesomeIcon className="icon" icon={faCircleCheck} />
                  <p>{gT(t, '.empty-triggers-sign.no-triggers')}</p>
                </div>
              ) : (
                <>
                  {translatedTriggers.map((trigger) => (
                    <StandaloneTrigger
                      trigger={trigger}
                      key={`kyc-standalone-trigger-${trigger.id}`}
                      t={t}
                    />
                  ))}
                  {kycStatus.websiteScanHits.map((websiteHit) => (
                    <WebsiteTrigger
                      websiteHit={websiteHit}
                      key={`kyc-website-trigger-${websiteHit.id}`}
                      t={t}
                    />
                  ))}
                  {kycStatus.boardMemberDeviations.map((deviation) => (
                    <BoardMemberDeviationTrigger
                      deviation={deviation}
                      key={`kyc-deviation-trigger-${deviation.id}`}
                      t={t}
                    />
                  ))}
                  {kycStatus.screeningResults.map((result) => (
                    <ScreeningResultTrigger
                      result={result}
                      key={`kyc-screening-trigger-${result.id}`}
                      onClick={() => onResultClick(result)}
                      t={t}
                    />
                  ))}
                </>
              )}
            </div>
          )}
      </div>
      {showAddSupportInfoModal && (
        <AddSupportInfoModal
          handleClose={() => {
            setShowAddSupportInfoModal(false);
            setIsCreatingNew(false);
          }}
          onSubmit={updateCustomerAndCreateNewProcess}
          customer={customerData}
          t={t}
        />
      )}
      {showConfirmActionModal && (
        <ConfirmActionModal
          description={confirmActionText}
          actionButtonText={
            confirmAction === ACTION.CANCEL
              ? gT(t, '.action-btn-text.cancel')
              : confirmAction === ACTION.NEW
              ? gT(t, '.action-btn-text.new')
              : ''
          }
          onConfirm={
            confirmAction === ACTION.CANCEL
              ? onCancelClick
              : confirmAction === ACTION.NEW
              ? onCreateClick
              : null
          }
          danger={confirmAction === ACTION.CANCEL}
          handleClose={() => setShowConfirmActionModal(false)}
          t={t}
        />
      )}
    </div>
  );
}
